import axios from "axios";
import {
  getApiErrorMessage,
  authorizedGetRequest,
  patchRequest,
  multipartPostRequest,
} from "./httpClient";

// Tickets API surface exposed via useContextStateManagement(). Needs
// PracticeId and the logged-in user (for user.token) from the provider, so
// it's a small factory called on every ContextApi render rather than a
// standalone module like httpClient.js - same call signatures as before,
// so nothing at any call site changes.
export const createTicketsApi = ({ PracticeId, user }) => {
  const createTicket = async (payload) => {
    const formData = new FormData();

    formData.append("PracticeId", PracticeId || "");
    formData.append("first_name", payload.firstName || "");
    formData.append("last_name", payload.lastName || "");
    formData.append("email", payload.email || "");
    formData.append("issue", payload.issue || "");
    formData.append("priority", payload.priority || "medium");
    formData.append("description", payload.description || "");

    const files = Array.isArray(payload.attachments) ? payload.attachments : [];
    files.forEach((file) => formData.append("attachments", file));

    return multipartPostRequest(user.token, "/api/tickets", formData);
  };

  const getMyTickets = async () =>
    authorizedGetRequest(user.token, "/api/tickets/my", {
      PracticeId,
    });

  const getAllTickets = async (options = {}) =>
    authorizedGetRequest(
      user.token,
      "/api/tickets/all",
      options.scope === "board"
        ? {
            scope: "board",
            ...(options.includeStats ? { includeStats: true } : {}),
          }
        : {
            PracticeId,
          },
    );

  const getTicketById = async (ticketId, options = {}) =>
    authorizedGetRequest(
      user.token,
      `/api/tickets/${ticketId}`,
      options.scope === "board"
        ? { scope: "board" }
        : {
            PracticeId,
          },
    );

  const updateTicketStatus = async (ticketId, status, options = {}) =>
    patchRequest(
      user.token,
      `/api/tickets/${ticketId}/status`,
      options.scope === "board"
        ? {
            status,
            scope: "board",
          }
        : {
            status,
            PracticeId,
          },
      options.scope === "board"
        ? { scope: "board" }
        : {
            PracticeId,
        },
    );

  const updateTicketType = async (ticketId, type, options = {}) =>
    patchRequest(
      user.token,
      `/api/tickets/${ticketId}/type`,
      options.scope === "board"
        ? {
            type,
            scope: "board",
          }
        : {
            type,
            PracticeId,
          },
      options.scope === "board"
        ? { scope: "board" }
        : {
            PracticeId,
          },
    );

  const addTicketComment = async (ticketId, commentText, options = {}) => {
    if (!user.token) {
      throw new Error("User token is missing");
    }

    try {
      const response = await axios.post(
        `/api/tickets/${ticketId}/comment`,
        options.scope === "board"
          ? { comment_text: commentText, scope: "board" }
          : { comment_text: commentText, PracticeId },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error));
    }
  };

  const sendTicketEmail = async (ticketId, payload, options = {}) => {
    if (!user.token) {
      throw new Error("User token is missing");
    }

    try {
      const response = await axios.post(
        `/api/tickets/${ticketId}/email-chain/send`,
        options.scope === "board"
          ? {
              subject: payload.subject || "",
              bodyText: payload.bodyText || "",
              scope: "board",
            }
          : {
              subject: payload.subject || "",
              bodyText: payload.bodyText || "",
              PracticeId,
            },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error));
    }
  };

  const getTicketAssigneeSuggestions = async () =>
    authorizedGetRequest(user.token, "/api/tickets/assignees/suggestions");

  const assignTicket = async (ticketId, payload, options = {}) => {
    if (!user.token) {
      throw new Error("User token is missing");
    }

    try {
      const response = await axios.patch(
        `/api/tickets/${ticketId}/assignment`,
        options.scope === "board"
          ? {
              assigneeEmail: payload.assigneeEmail || payload.email || "",
              assigneeName: payload.assigneeName || payload.displayName || "",
              scope: "board",
            }
          : {
              assigneeEmail: payload.assigneeEmail || payload.email || "",
              assigneeName: payload.assigneeName || payload.displayName || "",
              PracticeId,
            },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error));
    }
  };

  const uploadTicketAttachment = async (ticketId, attachments) => {
    const formData = new FormData();
    formData.append("PracticeId", PracticeId || "");
    const files = Array.isArray(attachments) ? attachments : [attachments];
    files.forEach((file) => formData.append("attachments", file));

    return multipartPostRequest(
      user.token,
      `/api/tickets/${ticketId}/attachment`,
      formData,
    );
  };

  return {
    createTicket,
    getMyTickets,
    getAllTickets,
    getTicketById,
    updateTicketStatus,
    updateTicketType,
    addTicketComment,
    sendTicketEmail,
    getTicketAssigneeSuggestions,
    assignTicket,
    uploadTicketAttachment,
  };
};
